import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { Link, Skeleton } from 'base-ui'

import { useOpenableControls } from '../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../global-config'
import { priceGetFormattedPrice } from '../../../../price'
import { ProductDeliveryInfoDialog } from '../../ProductDeliveryInfoDialog'
import { useProductPageContext } from '../../productPageContext'

const calculateDiscountPercents = ({
  displayPrice,
  locale,
  strikeThroughPrice,
}: {
  displayPrice: number
  locale: string
  strikeThroughPrice: number
}): string | undefined => {
  const rounded = 100 - (displayPrice / strikeThroughPrice) * 100
  if (rounded > 0) {
    return rounded.toLocaleString(locale, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    })
  }

  return undefined
}

type ProductRecommendationWidgetBuyTogetherPricesProps = {
  recommendedProduct?: GkAirRecommendedProductDataWeb
}

export const ProductRecommendationWidgetBuyTogetherPrices: FC<
  ProductRecommendationWidgetBuyTogetherPricesProps
> = ({ recommendedProduct }) => {
  const intl = useIntl()

  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)
  const { locale } = useGlobalConfigContext()
  const { currentOffer } = useProductPageContext()

  if (!recommendedProduct) {
    return <Skeleton className="h-20 w-40" variant="rectangular" />
  }

  const displayPrice =
    (currentOffer?.offerPrice?.price ?? 0) + Number(recommendedProduct.price)

  const strikeThroughPrice =
    (currentOffer?.strikeThroughOfferPrice?.price ?? 0) +
    Number(recommendedProduct.strike_through_price ?? '0')
  const discountPercent = calculateDiscountPercents({
    displayPrice,
    locale,
    strikeThroughPrice,
  })

  const isDiscountedPrice = strikeThroughPrice > displayPrice

  return (
    <div className="flex flex-col items-center">
      {isDiscountedPrice ? (
        <div className="self-start text-xs text-dark-primary-strong">
          <FormattedMessage id="general.label.listPrice" />
        </div>
      ) : null}
      <div className="flex">
        {isDiscountedPrice ? (
          <div className="mr-3.5 text-xl text-dark-primary-strong line-through">
            {priceGetFormattedPrice(strikeThroughPrice, intl)}
          </div>
        ) : null}
        <span
          className="text-xl font-bold text-dark-brand"
          data-qa-id="buy-together-total-price"
        >
          {priceGetFormattedPrice(displayPrice, intl)}
        </span>
      </div>
      <div className="inline-flex items-center text-xs">
        <FormattedMessage id="general.label.vatExclShipping" />
        <Link
          className="ml-1"
          onClick={handleOpen}
          slots={{ root: 'button' }}
          type="button"
        >
          <FormattedMessage id="general.label.shipping" />
        </Link>
      </div>
      {isDiscountedPrice && discountPercent ? (
        <div className="font-bold">
          <FormattedMessage
            id="general.label.savings"
            values={{
              discountPercent,
            }}
          />
        </div>
      ) : null}
      <ProductDeliveryInfoDialog isOpen={isOpen} onClose={handleClose} />
    </div>
  )
}
